import { z } from "zod";
import { isIanaTimeZone } from "./time-zone";

/** API v1 contracts. Additive changes only; a breaking change is a new version. */

export const ErrorResponse = z.object({ error: z.string() });

export const HealthResponse = z.object({ ok: z.literal(true), service: z.string(), environment: z.string() });

// ---------------------------------------------------------------------------
// Identity: who the caller is, resolved on the server (docs/permissions.md)
// ---------------------------------------------------------------------------

export const MembershipSummary = z.object({
  organizationId: z.string().uuid(),
  organizationName: z.string(),
  role: z.enum(["owner", "admin", "member"]),
  locationIds: z.array(z.string().uuid()),
  permissions: z.array(z.string()),
});

export const EntitlementSummary = z.object({
  product: z.enum(["learn", "rep-training", "clinical"]),
  via: z.enum(["user", "organization"]),
  organizationId: z.string().uuid().optional(),
  endsAt: z.string().datetime().optional(),
});

export const MeResponse = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().optional(),
    name: z.string().optional(),
    mfa: z.boolean(),
    groups: z.array(z.string()),
  }),
  memberships: z.array(MembershipSummary),
  entitlements: z.array(EntitlementSummary),
  /** True when a representative record exists for the caller (ADR 0012). */
  representative: z.boolean(),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ---------------------------------------------------------------------------
// Catalog: the curriculum snapshot as learners see it (no answer keys)
// ---------------------------------------------------------------------------

/**
 * The URL and storage form of a course or class code (training architecture §5, Course codes):
 * the code is shown as written, `LEAD 201-01`, and URLs and file keys use the lowercase slug,
 * `lead-201-01`.
 */
export function codeSlug(code: string): string {
  return code.trim().toLowerCase().replace(/\s+/g, "-");
}

/** True when `value` is the code itself or its slug. */
export function matchesCode(code: string, value: string): boolean {
  return code === value || codeSlug(code) === value.trim().toLowerCase();
}

export const CatalogClass = z.object({
  code: z.string(),
  slug: z.string(),
  title: z.string(),
  sequence: z.number().int(),
  topics: z.array(z.string()),
  estimatedMinutes: z.number().int().nullable(),
  /** Media kinds published for this class; empty means "coming soon". */
  media: z.array(z.enum(["video", "captions", "transcript", "handout", "job-aid"])),
  /** Code of the knowledge check that follows this class, if any. */
  checkCode: z.string().nullable(),
  /**
   * True when that check has items a learner can answer. A mapped check with no
   * items is still in production: the cards say so instead of offering it.
   */
  checkAuthored: z.boolean(),
});
export type CatalogClass = z.infer<typeof CatalogClass>;

export const CatalogModule = z.object({
  code: z.string(),
  slug: z.string(),
  sectionId: z.string(),
  title: z.string(),
  sequence: z.number().int(),
  releaseVersion: z.string(),
  releaseStage: z.enum(["mapped", "drafting", "functional-review", "pilot-approved", "use-approved", "superseded"]),
  classes: z.array(CatalogClass),
});
export type CatalogModule = z.infer<typeof CatalogModule>;

export const CatalogSection = z.object({
  id: z.string(),
  levelNumber: z.number().int(),
  title: z.string(),
  sequence: z.number().int(),
  pathway: z.boolean(),
});

export const CatalogPathway = z.object({
  id: z.string(),
  title: z.string(),
  examples: z.string(),
  sectionIds: z.array(z.string()),
  optionalSectionIds: z.array(z.string()),
  boundary: z.string(),
});

export const CatalogResponse = z.object({
  version: z.string(),
  source: z.object({ path: z.string(), version: z.string(), status: z.string() }),
  levels: z.array(z.object({ id: z.string(), number: z.number().int(), title: z.string() })),
  sections: z.array(CatalogSection),
  modules: z.array(CatalogModule),
  pathways: z.array(CatalogPathway),
});
export type CatalogResponse = z.infer<typeof CatalogResponse>;

// ---------------------------------------------------------------------------
// Learning: assignments, enrollments, progress, and the five records
// ---------------------------------------------------------------------------

export const AssignmentBasis = z.enum(["recommended", "recognition-prerequisite", "practice-assigned", "independent-requirement"]);

export const AssignmentSummary = z.object({
  id: z.string(),
  pathwayId: z.string(),
  organizationId: z.string().nullable(),
  basis: AssignmentBasis,
  createdAt: z.string(),
});

export const ClassProgressSummary = z.object({ classCode: z.string(), positionSeconds: z.number().int(), lastSeenAt: z.string() });
export const CompletionSummary = z.object({ classCode: z.string(), releaseVersion: z.string(), completedAt: z.string() });
export const KnowledgeResultSummary = z.object({
  checkCode: z.string(),
  attempt: z.number().int(),
  scorePercent: z.number().int(),
  criticalItemsPassed: z.boolean(),
  passed: z.boolean(),
  recordedAt: z.string(),
});

export const EnrollmentSummary = z.object({
  id: z.string(),
  moduleCode: z.string(),
  organizationId: z.string().nullable(),
  basis: AssignmentBasis,
  status: z.enum(["active", "completed", "withdrawn"]),
  releaseVersion: z.string(),
  createdAt: z.string(),
  progress: z.array(ClassProgressSummary),
  completions: z.array(CompletionSummary),
  knowledgeResults: z.array(KnowledgeResultSummary),
});
export type EnrollmentSummary = z.infer<typeof EnrollmentSummary>;

export const MyLearningResponse = z.object({
  assignments: z.array(AssignmentSummary),
  enrollments: z.array(EnrollmentSummary),
});
export type MyLearningResponse = z.infer<typeof MyLearningResponse>;

export const EnrollRequest = z.object({
  moduleCode: z.string(),
  /** Enroll under this organization's entitlement; omitted for a direct entitlement. Verified against the caller's memberships. */
  organizationId: z.string().uuid().optional(),
});
export const EnrollResponse = z.object({ enrollment: EnrollmentSummary, created: z.boolean() });

export const ProgressRequest = z.object({ positionSeconds: z.number().int().min(0) });
export const ProgressResponse = z.object({ classCode: z.string(), positionSeconds: z.number().int(), lastSeenAt: z.string() });

export const CompleteResponse = z.object({ completion: CompletionSummary, created: z.boolean() });

/** A knowledge-check item as the learner sees it: no answer, no critical flag. */
export const CheckItemPublic = z.object({
  id: z.string(),
  prompt: z.string(),
  options: z.array(z.object({ id: z.string(), text: z.string() })),
});

export const KnowledgeCheckPublic = z.object({
  code: z.string(),
  title: z.string(),
  kind: z.string(),
  moduleCode: z.string(),
  afterClassCode: z.string().nullable(),
  passingRule: z.object({ minPercent: z.number().int(), allCritical: z.boolean(), maxAttempts: z.number().int().nullable() }),
  items: z.array(CheckItemPublic),
  /** Draft labelling until the check is approved. */
  status: z.string(),
});
export type KnowledgeCheckPublic = z.infer<typeof KnowledgeCheckPublic>;

/** One key per intentional submission, reused unchanged after a transport failure. */
export const SubmissionKey = z.string().min(8).max(128).regex(/^[A-Za-z0-9_-]+$/);
export const AttemptRequest = z.object({ answers: z.record(z.string(), z.string()) });
export const AttemptResponse = z.object({
  result: KnowledgeResultSummary,
  /** Items answered incorrectly, with the remediation text authored for them. */
  remediation: z.array(z.object({ itemId: z.string(), remediation: z.string() })),
});
export type AttemptResponse = z.infer<typeof AttemptResponse>;

export const MediaUrlResponse = z.object({
  kind: z.enum(["video", "captions", "transcript", "handout", "job-aid"]),
  url: z.string(),
  expiresAt: z.string(),
});
export type MediaUrlResponse = z.infer<typeof MediaUrlResponse>;

/** Everything recorded about the caller, the five records shown separately (training architecture §6). */
export const MyRecordsResponse = z.object({
  completions: z.array(CompletionSummary.extend({ moduleCode: z.string() })),
  knowledgeResults: z.array(KnowledgeResultSummary.extend({ moduleCode: z.string() })),
  practicalEvidence: z.array(z.object({ activityCode: z.string(), standardVersion: z.string(), result: z.string(), performedOn: z.string() })),
  eligibility: z.array(z.object({ recognitionCode: z.string(), standardVersion: z.string(), eligible: z.boolean(), evaluatedAt: z.string() })),
  recognitions: z.array(z.object({ recognitionCode: z.string(), standardVersion: z.string(), issuer: z.string(), issuedAt: z.string(), expiresAt: z.string().nullable() })),
});
export type MyRecordsResponse = z.infer<typeof MyRecordsResponse>;

// ---------------------------------------------------------------------------
// Practice view: the organization's training roster (owners, admins, assigners)
// ---------------------------------------------------------------------------

export const RosterMember = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(["owner", "admin", "member"]),
  assignments: z.array(AssignmentSummary),
  summary: z.object({ modulesEnrolled: z.number().int(), classesCompleted: z.number().int(), classesTotal: z.number().int(), checksPassed: z.number().int() }),
});
export const PracticeRosterResponse = z.object({
  organization: z.object({ id: z.string(), name: z.string() }),
  members: z.array(RosterMember),
});
export type PracticeRosterResponse = z.infer<typeof PracticeRosterResponse>;

export const AssignPathwayRequest = z.object({
  userId: z.string(),
  pathwayId: z.string(),
  basis: AssignmentBasis.default("practice-assigned"),
});
export const AssignPathwayResponse = z.object({ assignment: AssignmentSummary, enrollmentsCreated: z.number().int() });

// ---------------------------------------------------------------------------
// Admin (staff): organizations, invitations, entitlements, learner progress
// ---------------------------------------------------------------------------

/** One current product entitlement of an organization as staff see it; the id is what ending it names. */
export const OrganizationEntitlementSummary = z.object({
  id: z.string(),
  product: z.enum(["learn", "rep-training", "clinical"]),
  source: z.enum(["manual", "stripe"]),
  grantedAt: z.string(),
  endsAt: z.string().nullable(),
});
export const OrganizationSummary = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(["active", "suspended", "closed"]),
  locations: z.number().int(),
  members: z.number().int(),
  /** The products the organization currently holds (active entitlements that have not ended). */
  products: z.array(z.string()),
  /** The entitlements behind `products`, with their ids. */
  entitlements: z.array(OrganizationEntitlementSummary),
  createdAt: z.string(),
});
export const InvitationReferencesResponse = z.object({ organizations: z.array(OrganizationSummary), pathways: z.array(CatalogPathway) });
export type InvitationReferencesResponse = z.infer<typeof InvitationReferencesResponse>;
export const OrganizationsResponse = z.object({ organizations: z.array(OrganizationSummary) });
export const CreateOrganizationRequest = z.object({ name: z.string().min(2), slug: z.string().regex(/^[a-z0-9-]+$/).optional() });
export const CreateOrganizationResponse = z.object({ organization: OrganizationSummary });

export const InviteRequest = z.object({
  email: z.string().email(),
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  organizationId: z.string().uuid().optional(),
  role: z.enum(["owner", "admin", "member"]).default("member"),
  /**
   * Products to grant. `learn` and `rep-training` become direct entitlements; an organization member usually
   * inherits the organization's instead. `clinical` is never a direct entitlement (ADR 0004): it needs an
   * organization that already holds clinical access and seeds the new membership with `clinical:read`.
   */
  products: z.array(z.enum(["learn", "rep-training", "clinical"])).default([]),
  pathwayId: z.string().optional(),
});
export const InviteResponse = z.object({ userId: z.string(), status: z.enum(["invited", "existing"]), enrollmentsCreated: z.number().int() });

export const GrantEntitlementRequest = z.object({
  product: z.enum(["learn", "rep-training", "clinical"]),
  subjectType: z.enum(["user", "organization"]),
  subjectId: z.string(),
  endsAt: z.string().datetime().optional(),
});
export const GrantEntitlementResponse = z.object({ entitlementId: z.string() });
/** The entitlement after `POST /admin/entitlements/:entitlementId/end`; ending one that has already ended changes nothing. */
export const EndEntitlementResponse = z.object({
  entitlement: z.object({
    id: z.string(),
    product: z.enum(["learn", "rep-training", "clinical"]),
    subjectType: z.enum(["user", "organization"]),
    subjectId: z.string(),
    status: z.enum(["active", "ended"]),
    endsAt: z.string().nullable(),
  }),
});

/**
 * The membership permissions the console can grant (docs/permissions.md; api-v2.md section 4.1). `clinical:write`
 * implies `clinical:read`; a membership normally holds read, write and order together. Anything else is rejected.
 */
export const KNOWN_PERMISSIONS = ["clinical:read", "clinical:write", "clinical:order", "clinical:manage", "learning:assign"] as const;
export const Permission = z.enum(KNOWN_PERMISSIONS);
export type Permission = z.infer<typeof Permission>;

/** One member of an organization as staff see it: the membership only, never anything clinical. */
export const OrganizationMemberSummary = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(["owner", "admin", "member"]),
  status: z.enum(["active", "suspended", "ended"]),
  permissions: z.array(z.string()),
  /** Location scope; empty means every location of the organization. */
  locationIds: z.array(z.string()),
});
export const OrganizationMembersResponse = z.object({
  organization: z.object({ id: z.string(), name: z.string() }),
  members: z.array(OrganizationMemberSummary),
  /** The organization's locations, for choosing a member's scope. */
  locations: z.array(z.object({ id: z.string(), name: z.string() })),
});
/**
 * Replaces a membership's permissions and location scope; every location must belong to the same organization.
 * `status` suspends, ends or reinstates the membership: only an active membership grants anything, so a suspended
 * or ended member loses the organization's products and permissions on their next request.
 */
export const UpdateMembershipRequest = z.object({
  permissions: z.array(Permission),
  locationIds: z.array(z.string().uuid()),
  role: z.enum(["owner", "admin", "member"]).optional(),
  status: z.enum(["active", "suspended", "ended"]).optional(),
});
export const UpdateMembershipResponse = z.object({ member: OrganizationMemberSummary });

/** The zone check a location's time zone must pass (time-zone.ts). */
export { isIanaTimeZone };
/**
 * A new location for an organization (`POST /admin/organizations/:organizationId/locations`, roadmap C5): tenancy
 * data only, the name and the zone its wall times are shown in. Its tray panel and allergen profile are set afterwards
 * on the same Locations screen (`PUT /admin/catalog/locations/:id`); schedule settings keep their defaults until the
 * clinic changes them. No address yet: the table has no column for one (sub-project 3).
 */
export const CreateLocationRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    timezone: z.string().trim().refine(isIanaTimeZone).default("America/Denver"),
  })
  .strict();
export const CreatedLocation = z.object({ id: z.string(), organizationId: z.string(), name: z.string(), timezone: z.string() });
export const CreateLocationResponse = z.object({ location: CreatedLocation });

export const LearnerSummary = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  organizations: z.array(z.object({ id: z.string(), name: z.string(), role: z.string() })),
  records: z.object({
    completions: z.number().int(),
    knowledgeResults: z.number().int(),
    practicalEvidence: z.number().int(),
    eligibility: z.number().int(),
    recognitions: z.number().int(),
  }),
  lastSeenAt: z.string().nullable(),
});
export const LearnersResponse = z.object({ learners: z.array(LearnerSummary) });
export const LearnerDetailResponse = z.object({ learner: LearnerSummary, learning: MyLearningResponse, records: MyRecordsResponse });

export const AuditEventSummary = z.object({
  id: z.string(),
  at: z.string(),
  actorUserId: z.string().nullable(),
  organizationId: z.string().nullable(),
  action: z.string(),
  subjectType: z.string(),
  subjectId: z.string(),
  outcome: z.string(),
});
export const AuditEventsResponse = z.object({ events: z.array(AuditEventSummary) });

/** `POST /admin/notifications/test` takes no body: the recipient is always the caller's own address. */
export const TestNotificationResponse = z.object({ notificationId: z.string() });

// ---------------------------------------------------------------------------
// Billing, staff side (docs/specs/2026-10-25-billing-plumbing-design.md, ADR 0008)
// ---------------------------------------------------------------------------

export const BILLING_PLANS = ["alc", "bas", "bas_m2m", "cor", "cus"] as const;
export const BillingPlan = z.enum(BILLING_PLANS);
export type BillingPlan = z.infer<typeof BillingPlan>;
/** A calendar date, `YYYY-MM-DD`. */
const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** The billing account of an organization as staff see it: Stripe identifiers and status, never card details. */
export const BillingAccount = z.object({
  organizationId: z.string(),
  plan: BillingPlan,
  effectiveOn: CalendarDate,
  termMonths: z.number().int(),
  stripeCustomerId: z.string().nullable(),
  paymentMethodStatus: z.enum(["none", "saved"]),
  programFeeSubscriptionId: z.string().nullable(),
  /** Stripe's own vocabulary: trialing, active, past_due, canceled, ... */
  programFeeSubscriptionStatus: z.string().nullable(),
  status: z.enum(["active", "suspended", "closed"]),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
/** The Stripe customer linked to the account, read from Stripe when the account is shown. */
export const BillingCustomerSummary = z.object({ id: z.string(), name: z.string().nullable(), email: z.string().nullable() });
export const BillingAccountResponse = z.object({
  organization: z.object({ id: z.string(), name: z.string() }),
  account: BillingAccount.nullable(),
  customer: BillingCustomerSummary.nullable(),
  /** Active locations: the Program Fee subscription quantity for Base and Core. */
  activeLocations: z.number().int(),
});
/**
 * Creates (version 0) or replaces the account. The Stripe customer is linked by `stripeCustomerId` when one
 * already exists (DocuSign makes its own); otherwise the platform creates one from the organization name and
 * `billingEmail`, which is required only then.
 */
export const UpsertBillingAccountRequest = z.object({
  version: z.number().int().min(0),
  plan: BillingPlan,
  effectiveOn: CalendarDate,
  termMonths: z.number().int().min(1).max(120).optional(),
  status: z.enum(["active", "suspended", "closed"]).optional(),
  stripeCustomerId: z.string().min(1).optional(),
  billingEmail: z.string().email().optional(),
});
export const UpsertBillingAccountResponse = z.object({ account: BillingAccount, customer: BillingCustomerSummary.nullable() });
/** `POST .../billing/setup-link` takes no body: the return URL is configured on the server. */
export const CreateSetupLinkResponse = z.object({ url: z.string(), sessionId: z.string() });
export const ProgramFeeSubscriptionSummary = z.object({ id: z.string(), status: z.string(), quantity: z.number().int(), billingCycleAnchor: z.string(), trialEnd: z.string() });
export const ProgramFeeStartResponse = z.object({ account: BillingAccount, subscription: ProgramFeeSubscriptionSummary });
export const ProgramFeeStopResponse = z.object({ account: BillingAccount, subscription: z.object({ id: z.string(), status: z.string() }) });
export type BillingAccount = z.infer<typeof BillingAccount>;
export type BillingCustomerSummary = z.infer<typeof BillingCustomerSummary>;
export type BillingAccountResponse = z.infer<typeof BillingAccountResponse>;
export type UpsertBillingAccountRequest = z.infer<typeof UpsertBillingAccountRequest>;
export type UpsertBillingAccountResponse = z.infer<typeof UpsertBillingAccountResponse>;
export type CreateSetupLinkResponse = z.infer<typeof CreateSetupLinkResponse>;
export type ProgramFeeStartResponse = z.infer<typeof ProgramFeeStartResponse>;
export type ProgramFeeStopResponse = z.infer<typeof ProgramFeeStopResponse>;

// ---------------------------------------------------------------------------
// Representative portal (ADR 0012) — no patient data, aggregate counts only
// ---------------------------------------------------------------------------

export const RepMeResponse = z.object({
  representative: z.object({ id: z.string(), code: z.string(), status: z.enum(["active", "inactive"]) }),
  practices: z.array(z.object({ organizationId: z.string(), name: z.string(), startsOn: z.string(), endsOn: z.string().nullable() })),
  /** Whether the caller holds the rep-training entitlement in the learning product. */
  training: z.object({ entitled: z.boolean() }),
});
export type RepMeResponse = z.infer<typeof RepMeResponse>;

export const RepVolumeResponse = z.object({
  months: z.array(z.object({ month: z.string(), organizationId: z.string(), name: z.string(), tests: z.number().int(), therapyOrders: z.number().int(), sourceSystem: z.string() })),
});
export type RepVolumeResponse = z.infer<typeof RepVolumeResponse>;

export const StatementLine = z.object({ organizationId: z.string().nullable(), description: z.string(), amountCents: z.number().int() });
export const CommissionStatementSummary = z.object({
  id: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  status: z.enum(["draft", "issued", "paid", "void"]),
  currency: z.string(),
  totalCents: z.number().int(),
  lines: z.array(StatementLine),
  planVersion: z.string().nullable(),
  issuedAt: z.string().nullable(),
});
export const RepStatementsResponse = z.object({ statements: z.array(CommissionStatementSummary) });
export type RepStatementsResponse = z.infer<typeof RepStatementsResponse>;

// Inferred types for every response and summary shape.
export type EnrollResponse = z.infer<typeof EnrollResponse>;
export type ProgressResponse = z.infer<typeof ProgressResponse>;
export type CompleteResponse = z.infer<typeof CompleteResponse>;
export type OrganizationsResponse = z.infer<typeof OrganizationsResponse>;
export type CreateOrganizationResponse = z.infer<typeof CreateOrganizationResponse>;
export type InviteResponse = z.infer<typeof InviteResponse>;
export type GrantEntitlementResponse = z.infer<typeof GrantEntitlementResponse>;
export type EndEntitlementResponse = z.infer<typeof EndEntitlementResponse>;
export type OrganizationMemberSummary = z.infer<typeof OrganizationMemberSummary>;
export type OrganizationMembersResponse = z.infer<typeof OrganizationMembersResponse>;
export type UpdateMembershipRequest = z.infer<typeof UpdateMembershipRequest>;
export type UpdateMembershipResponse = z.infer<typeof UpdateMembershipResponse>;
/** What a caller sends: `timezone` may be left out (America/Denver). */
export type CreateLocationRequest = z.input<typeof CreateLocationRequest>;
export type CreatedLocation = z.infer<typeof CreatedLocation>;
export type CreateLocationResponse = z.infer<typeof CreateLocationResponse>;
export type LearnerSummary = z.infer<typeof LearnerSummary>;
export type LearnersResponse = z.infer<typeof LearnersResponse>;
export type LearnerDetailResponse = z.infer<typeof LearnerDetailResponse>;
export type AuditEventsResponse = z.infer<typeof AuditEventsResponse>;
export type TestNotificationResponse = z.infer<typeof TestNotificationResponse>;
export type AssignPathwayResponse = z.infer<typeof AssignPathwayResponse>;
export type RosterMember = z.infer<typeof RosterMember>;
export type OrganizationSummary = z.infer<typeof OrganizationSummary>;
export type OrganizationEntitlementSummary = z.infer<typeof OrganizationEntitlementSummary>;
export type AssignmentSummary = z.infer<typeof AssignmentSummary>;
export type CommissionStatementSummary = z.infer<typeof CommissionStatementSummary>;
export type CatalogSection = z.infer<typeof CatalogSection>;
export type CatalogPathway = z.infer<typeof CatalogPathway>;
export type MembershipSummary = z.infer<typeof MembershipSummary>;
export type EntitlementSummary = z.infer<typeof EntitlementSummary>;
export type ClassProgressSummary = z.infer<typeof ClassProgressSummary>;
export type CompletionSummary = z.infer<typeof CompletionSummary>;
export type KnowledgeResultSummary = z.infer<typeof KnowledgeResultSummary>;
export type CheckItemPublic = z.infer<typeof CheckItemPublic>;
export type AuditEventSummary = z.infer<typeof AuditEventSummary>;

export * from "./billing-supplies";
/** Supply ordering: practice placement and staff confirm, ship, cancel (ADR 0008). */
export * from "./billing-orders";
